import React from "react";
import { Popover } from "antd";

import { buildClassName } from "shared/utils/vega";

import StatusIcon, { getStatusColor } from "../StatusIcon";

// Helper function to get the appropriate popover title based on status
const getPopoverTitle = (status) => {
  if (status === "fail") {
    return "Requirements Not Met";
  }
  if (status === "warn") {
    return "Conditionally Failed Requirements";
  }
};

// Helper function to create popover content based on details format
const createPopoverContent = (details, status) => {
  if (!details) return null;

  const popoverTitle = getPopoverTitle(status);

  // Handle array of objects with title/description from getOutcomeDetailsFromApplicantData
  if (
    Array.isArray(details) &&
    details.length > 0 &&
    typeof details[0] === "object"
  ) {
    // Check if these are explanation objects with title/description
    if ("title" in details[0] || "description" in details[0]) {
      return (
        <div className="vega content popover">
          <h4
            style={{
              margin: "0 0 8px 0",
              fontSize: "14px",
              fontWeight: "500",
              color: getStatusColor(status),
            }}
          >
            {popoverTitle}
          </h4>
          <ul style={{ margin: 0, paddingLeft: "15px" }}>
            {details.map((item, idx) => (
              <li key={`detail-item-${item.title || idx}`}>
                {item.title && item.description ? (
                  <>
                    <strong>{item.title}:</strong> {item.description}
                  </>
                ) : (
                  item.title || item.description || ""
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }
  }

  // Check if it's a single structured detail object
  const isStructuredDetails =
    typeof details === "object" && !Array.isArray(details);

  if (isStructuredDetails) {
    // Render structured details with title and description on a single line
    return (
      <div className="vega content popover">
        <h4
          style={{
            margin: "0 0 8px 0",
            fontSize: "14px",
            fontWeight: "500",
            color: getStatusColor(status),
          }}
        >
          {popoverTitle}
        </h4>
        <p
          style={{
            margin: "0",
            fontSize: "13px",
            lineHeight: "1.4",
          }}
        >
          {details.title && details.description ? (
            <>
              <strong>{details.title}:</strong> {details.description}
            </>
          ) : (
            details.title || details.description || ""
          )}
        </p>
      </div>
    );
  }

  if (Array.isArray(details) && details.length > 0) {
    // Render array of strings as bullet points
    return (
      <div className="vega content popover">
        <h4
          style={{
            margin: "0 0 8px 0",
            fontSize: "14px",
            fontWeight: "500",
            color: getStatusColor(status),
          }}
        >
          {popoverTitle}
        </h4>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          {details.map((item, idx) => (
            <li
              key={`detail-item-${
                typeof item === "string" ? item.substring(0, 10) : idx
              }`}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
};

const LumenEntryCard = ({ title, label, status, details }) => {
  const cardStatus = status;
  const hasDetails = !!details;
  const popoverContent = createPopoverContent(details, cardStatus);
  // Disable popover for now
  const disablePopover = true;

  const cardContent = (
    <div
      className={buildClassName([
        "vega flex gap small column card service",
        "status-bar",
        cardStatus,
      ])}
      style={{
        borderLeft: `4px solid ${getStatusColor(cardStatus)}`,
        paddingLeft: "12px",
        cursor: hasDetails && !disablePopover ? "pointer" : "default",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        height: "auto",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: "12px 12px 12px 12px",
      }}
    >
      <div
        className="vega flex gap small column"
        style={{ width: "100%", alignSelf: "flex-start" }}
      >
        <div className="vega flex gap small items-center">
          <StatusIcon status={cardStatus} />
          {title && (
            <span
              className="vega title"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {title}
            </span>
          )}
        </div>
      </div>

      {label && (
        <div
          className="vega label"
          style={{
            overflow: "hidden",
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            width: "100%",
            marginTop: "8px",
          }}
        >
          {label}
        </div>
      )}
    </div>
  );

  // Disable popover for now

  return hasDetails && !disablePopover ? (
    <Popover content={popoverContent} trigger="hover" placement="right">
      {cardContent}
    </Popover>
  ) : (
    cardContent
  );
};

export default LumenEntryCard;
